import React, { useEffect, useState } from 'react';
import { api, API_BASE } from '../../api/client';
import axios from 'axios';

interface Category { id: number; title: string; description: string; language: string; }

const EMPTY = { title: '', description: '', language: 'ru' };

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API_BASE}/categories/get-all?size=100`).then(r => setCategories(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const startEdit = (c: Category) => {
    setEditId(c.id);
    setForm({ title: c.title, description: c.description, language: c.language || 'ru' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancel = () => { setEditId(null); setForm(EMPTY); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/categories/update/${editId}`, form);
      } else {
        await api.post('/categories/create', form);
      }
      cancel();
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Удалить категорию? Все документы этой категории останутся без категории.')) return;
    await api.delete(`/categories/delete/${id}`);
    await load();
  };

  return (
    <div style={s.page}>
      <h2 style={s.pageTitle}>Категории документов</h2>

      {/* Форма */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>{editId ? '✏️ Редактировать категорию' : '➕ Новая категория'}</h3>
        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>Язык *</label>
            <select
              style={s.input}
              value={form.language}
              onChange={e => setForm({ ...form, language: e.target.value })}
            >
              <option value="ru">🇷🇺 Русский</option>
              <option value="ky">🇰🇬 Кыргызский</option>
              <option value="en">🇬🇧 English</option>
            </select>
          </div>
          <input
            style={s.input}
            placeholder="Название категории *"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            required
          />
          <textarea
            style={{ ...s.input, resize: 'vertical' }}
            placeholder="Описание (необязательно)"
            rows={3}
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.btnPrimary} type="submit" disabled={saving}>
              {saving ? 'Сохранение...' : editId ? '💾 Сохранить' : '➕ Создать'}
            </button>
            {editId && <button type="button" onClick={cancel} style={s.btnCancel}>Отмена</button>}
          </div>
        </form>
      </div>

      {/* Список */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Все категории ({categories.length})</h3>
        {categories.length === 0 ? (
          <p style={s.empty}>Категорий пока нет</p>
        ) : (
          categories.map(c => (
            <div key={c.id} style={s.row}>
              <div style={s.rowInfo}>
                <div style={s.rowMeta}>
                  <span style={s.langBadge}>{c.language === 'ru' ? '🇷🇺' : c.language === 'ky' ? '🇰🇬' : '🇬🇧'}</span>
                </div>
                <div style={s.rowTitle}>📁 {c.title}</div>
                {c.description && <div style={s.rowDesc}>{c.description}</div>}
              </div>
              <button onClick={() => startEdit(c)} style={s.btnEdit}>✏️</button>
              <button onClick={() => remove(c.id)} style={s.btnDanger}>🗑</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { padding: 'clamp(16px, 4vw, 32px)', maxWidth: 800, margin: '0 auto' },
  pageTitle: { color: '#1a3a6b', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', marginBottom: 'clamp(16px, 3vw, 24px)' },
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 16, overflow: 'hidden' },
  cardTitle: { color: '#1a3a6b', fontSize: 'clamp(0.9rem, 2vw, 1rem)', padding: '16px 20px', borderBottom: '1px solid #f0f4fb' },
  form: { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 20px' },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: '0.85rem', fontWeight: 600, color: '#444' },
  input: { padding: '10px 14px', borderRadius: 8, border: '1px solid #dde3ef', fontSize: '0.95rem', outline: 'none', width: '100%' },
  btnPrimary: { padding: '10px 24px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  btnCancel: { padding: '10px 20px', background: '#f0f4fb', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer' },
  empty: { padding: '16px 20px', color: '#888', fontSize: '0.9rem' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderTop: '1px solid #f0f4fb', flexWrap: 'wrap' },
  rowInfo: { flex: 1, minWidth: 200 },
  rowMeta: { marginBottom: 4 },
  langBadge: { fontSize: '0.85rem', fontWeight: 600 },
  rowTitle: { fontWeight: 700, color: '#1a2a4a', fontSize: '0.95rem', marginBottom: 2, wordBreak: 'break-word' },
  rowDesc: { fontSize: '0.85rem', color: '#666', wordBreak: 'break-word' },
  btnEdit: { padding: '6px 10px', background: '#f39c12', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 },
  btnDanger: { padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 },
};
